import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { ColDef, ColGroupDef, GridApi, GridOptions } from 'ag-grid-community';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AcademicService } from 'src/app/services/academic.service';
import { DateFilterComponent, DateFilterRange } from '../date-filter/date-filter.component';
import { ReportColumn } from '../column-picker/column-picker.component';
import {
  EmailReportDialogComponent,
  EmailReportDialogData,
  EmailReportDialogResult
} from '../email-report-dialog/email-report-dialog.component';
import {
  ExportColumn,
  ExportContext,
  ReportExportService
} from 'src/app/services/report-export.service';
import { ReportEmailService } from 'src/app/services/report-email.service';
import { FilterFieldDef, FilterRule } from 'src/app/shared/utils/field-filters.types';
import { countActiveRules } from 'src/app/shared/utils/field-filters.util';
import { ReportStateService } from 'src/app/services/report-state.service';
import { FavouriteConfig, ReportStateV1 } from 'src/app/shared/utils/report-state.types';
import {
  FavouriteReportEntry,
  FavouriteReportService
} from 'src/app/services/favourite-report.service';

/**
 * Standard layout that every revamped report renders inside.
 *
 * Owns: page header, optional info banner (e.g. for AMBER data caveats),
 * filter strip (ng-content + standard DateFilterComponent),
 * action bar (Refresh / Columns / Export Excel / Export CSV / Email / â˜… Favourite),
 * and the AG-Grid container.
 *
 * ## Persistent state
 * When `moduleName` is supplied, the layout automatically saves and restores:
 *   - Advanced filter rules (field-filter panel)
 *   - Date filter mode + range
 *   - AG-Grid pagination page size
 *   - Advanced filter panel open/closed state
 *
 * Column visibility is persisted independently by `ColumnPickerComponent`
 * using a user-scoped localStorage key.
 *
 * The parent can listen to `(stateRestored)` to receive the full saved state
 * object and restore its own custom filters from `state.customFilters`.
 */
@Component({
  selector: 'app-report-page-layout',
  templateUrl: './report-page-layout.component.html',
  styleUrls: ['./report-page-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReportPageLayoutComponent
  implements OnInit, AfterViewInit, OnChanges, OnDestroy {

  // â”€â”€ Identity â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  @Input() title = '';
  @Input() reportSlug = '';
  @Input() reportName = '';
  @Input() backLink: any[] | string | null = ['/nora/fee-management/reports'];
  @Input() infoBanner: string | null = null;

  /**
   * Module key used for state storage and favourites, e.g. 'fee-management'.
   * Required to enable automatic state persistence and the favourite button.
   */
  @Input() moduleName = '';
  /** Human-readable module label shown on the Favourites card. */
  @Input() moduleLabel = '';
  /** Short description of the report â€” included in the Favourites card. */
  @Input() reportDescription = '';
  /**
   * Show the â˜… Favourite button in the action bar.
   * Requires `moduleName` to be set for the button to function.
   */
  @Input() enableFavourite = false;

  // â”€â”€ Data â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  @Input() loading = false;
  /** Columns for the picker (and for filtering exports). Must align with colDefs by `key === field`. */
  @Input() columns: ReportColumn[] = [];
  /** AG-Grid column definitions. */
  /** Accepts grouped (banded) column definitions too, so a report can render a two-row header
   *  (e.g. "Tuition Fees" spanning its month columns). Plain ColDef[] behaves exactly as before. */
  @Input() colDefs: (ColDef | ColGroupDef)[] = [];
  /** Data rows (filtered â€” passed to the AG-Grid). */
  @Input() rows: any[] = [];
  /** Unfiltered full dataset â€” passed to the filter panel for accurate match-count preview. */
  @Input() allRows: any[] = [];
  /** Optional override of how each column exports (label / formatter). */
  @Input() exportColumns: ExportColumn[] | null = null;

  // â”€â”€ Date filter â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  @Input() showDateFilter = true;
  @Input() defaultDateMode = 'thisMonth';
  @Input() academicYearStart: Date | string | null = null;
  @Input() academicYearEnd: Date | string | null = null;

  // â”€â”€ Email â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  @Input() showEmail = false;

  // â”€â”€ Academic year â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  @Input() showAcademicYearFilter = true;

  // â”€â”€ Mandatory filter â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  @Input() showMandatoryFilter = false;
  @Input() mandatoryValue: 'true' | 'false' | '' = 'true';
  @Output() mandatoryChange = new EventEmitter<'true' | 'false' | ''>();

  // â”€â”€ Clear filter â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  @Input() showClearFilter = false;

  // â”€â”€ Export / email identifiers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  @Input() schoolId = 0;
  @Input() academicYearId = 0;
  @Input() schoolCode = '';
  @Input() academicYearLabel = '';
  @Input() currentFilters: Record<string, any> = {};
  @Input() filterSummary: string[] = [];

  // â”€â”€ Grid settings â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  @Input() rowSelection: 'single' | 'multiple' | undefined = undefined;
  @Input() paginationPageSize = 25;
  @Input() paginationPageSizeSelector: number[] | boolean = [10, 25, 50, 100];

  // â”€â”€ Advanced filter â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  @Input() filterDefs: FilterFieldDef[] = [];

  // â”€â”€ Outputs â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  @Output() dateRange = new EventEmitter<DateFilterRange>();
  @Output() refresh = new EventEmitter<void>();
  @Output() gridReady = new EventEmitter<GridApi>();
  @Output() academicYearChange = new EventEmitter<{
    id: number; label: string; startDate: Date | null; endDate: Date | null;
  }>();
  @Output() clearFilters = new EventEmitter<void>();
  @Output() filterRulesChange = new EventEmitter<FilterRule[]>();

  /**
   * Emitted once in AfterViewInit after the layout has loaded and applied
   * the saved state.  The parent component should listen to this event and
   * restore its own custom filters from `state.customFilters`.
   *
   * If no saved state exists, this event is not emitted.
   */
  @Output() stateRestored = new EventEmitter<ReportStateV1>();

  // â”€â”€ Internal state â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  academicYearList: any[] = [];
  visibleKeys = new Set<string>();
  emailing = false;
  filterPanelOpen = false;
  activeRuleCount = 0;
  dateWarning: string | null = null;
  lastAppliedRules: FilterRule[] = [];

  /** Favourite state (shown in the star button). */
  isFavourite = false;
  /** Inline "save config" prompt shown when the star is first clicked. */
  showFavNamePrompt = false;
  favConfigName = '';

  gridOptions: GridOptions = {
    defaultColDef: { resizable: true, sortable: true, filter: true },
    pagination: true,
    paginationPageSize: 25,
    suppressRowClickSelection: true
  };

  private api: GridApi | null = null;
  private readonly _destroy$ = new Subject<void>();

  /** Tracks the last date-range emitted so we can persist it. */
  private _lastDateRange: DateFilterRange | null = null;

  @ViewChild(DateFilterComponent) private dateFilterRef?: DateFilterComponent;

  constructor(
    private dialog: MatDialog,
    private exportSvc: ReportExportService,
    private emailSvc: ReportEmailService,
    private academic: AcademicService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private reportStateSvc: ReportStateService,
    private favSvc: FavouriteReportService
  ) {
    this.loadAcademicYears();
  }

  ngOnInit(): void {
    // Restore filter rules and filter-panel open state from persisted state.
    // Column visibility is handled by ColumnPickerComponent automatically.
    // Date filter is restored in AfterViewInit once the child ViewChild is ready.
    if (this.moduleName && this.reportSlug) {
      const state = this._loadSavedState();
      if (state) {
        if (state.filterRules?.length) {
          this.lastAppliedRules = state.filterRules;
          this.activeRuleCount = countActiveRules(state.filterRules);
        }
        if (state.advancedFilterOpen) {
          this.filterPanelOpen = true;
        }
      }
      this._refreshFavouriteStatus();
      // Listen for external favourite changes (e.g. from the landing page).
      this.favSvc.getAll()
        .pipe(takeUntil(this._destroy$))
        .subscribe(() => {
          this._refreshFavouriteStatus();
          this.cdr.markForCheck();
        });
    }
  }

  ngAfterViewInit(): void {
    if (!this.moduleName || !this.reportSlug) return;
    const state = this._loadSavedState();
    if (!state) return;
    // Restore date filter (ViewChild is available now).
    if (state.dateFilterMode && this.dateFilterRef && this.showDateFilter) {
      // Small timeout to avoid ExpressionChangedAfterItHasBeenCheckedError
      // because ngAfterViewInit runs inside the current change-detection cycle.
      setTimeout(() => {
        this.dateFilterRef?.restoreState(
          state.dateFilterMode!,
          state.dateFromDate,
          state.dateToDate
        );
      }, 0);
    }
    // Notify parent so it can restore its own custom filters.
    this.stateRestored.emit(state);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['paginationPageSize']) {
      this.gridOptions = { ...this.gridOptions, paginationPageSize: this.paginationPageSize };
    }
    if (changes['rows'] && this.api) {
      this.syncOverlayState();
    }
    if (changes['loading'] && this.api) {
      this.syncOverlayState();
    }
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  // â”€â”€ Grid events â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  onGridReady(event: { api: GridApi }): void {
    this.api = event.api;
    this.applyVisibility();
    this.syncOverlayState();
    this.gridReady.emit(this.api);
    // Restore saved pagination page size.
    if (this.moduleName && this.reportSlug) {
      const state = this._loadSavedState();
      if (state?.pageSize) {
        // Update gridOptions so the page size selector also reflects the restored value.
        this.gridOptions = { ...this.gridOptions, paginationPageSize: state.pageSize };
        this.cdr.markForCheck();
      }
    }
    // Persist page-size changes made by the user through the grid UI.
    this.api.addEventListener('paginationChanged', () => {
      if (!this.api || !this.moduleName || !this.reportSlug) return;
      const size = this.api.paginationGetPageSize();
      if (size && size !== this.paginationPageSize) {
        this.reportStateSvc.save(this.reportSlug, this.moduleName, { pageSize: size });
      }
    });
  }

  // â”€â”€ Row data â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  get gridRowData(): any[] | null {
    return this.loading && (!this.rows || this.rows.length === 0) ? null : (this.rows ?? []);
  }

  // â”€â”€ Column visibility â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  onVisibilityChange(visible: Set<string>): void {
    this.visibleKeys = visible;
    this.applyVisibility();
    if (this.moduleName && this.reportSlug) {
      this.reportStateSvc.save(this.reportSlug, this.moduleName, {
        visibleColumns: Array.from(visible)
      });
    }
    this.cdr.markForCheck();
  }

  // â”€â”€ Date range â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  onDateRangeChange(range: DateFilterRange): void {
    if (range.crossesMonths) {
      const modeLabel = range.mode === 'thisWeek' ? 'This Week' : 'Previous Week';
      this.dateWarning = `"${modeLabel}" spans two months (${range.fromDate} to ${range.toDate}). Results may be incomplete use "Custom" date range for a complete view.`;
    } else {
      this.dateWarning = null;
    }
    this._lastDateRange = range;
    if (this.moduleName && this.reportSlug) {
      this.reportStateSvc.save(this.reportSlug, this.moduleName, {
        dateFilterMode: range.mode,
        dateFromDate: range.fromDate || undefined,
        dateToDate: range.toDate || undefined
      });
    }
    this.cdr.markForCheck();
    this.dateRange.emit(range);
  }

  // â”€â”€ Academic year â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  onAcademicYearPick(value: any): void {
    const id = Number(value);
    if (!id) return;
    const found = this.academicYearList.find((ay: any) =>
      (ay.academicYearId ?? ay.id ?? ay.AcademicYearId) === id);
    if (!found) return;
    const startDate = found.startDate ? new Date(found.startDate) : null;
    const endDate = found.endDate ? new Date(found.endDate) : null;
    const label = found.description ?? found.name ?? '';
    if (this.moduleName && this.reportSlug) {
      this.reportStateSvc.save(this.reportSlug, this.moduleName, { academicYearId: id });
    }
    this.academicYearChange.emit({ id, label, startDate, endDate });
  }

  // â”€â”€ Filter panel â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  openFilterPanel(): void {
    this.filterPanelOpen = true;
    if (this.moduleName && this.reportSlug) {
      this.reportStateSvc.save(this.reportSlug, this.moduleName, { advancedFilterOpen: true });
    }
  }

  onPanelRulesApplied(rules: FilterRule[]): void {
    this.lastAppliedRules = rules;
    this.activeRuleCount = countActiveRules(rules);
    if (this.moduleName && this.reportSlug) {
      this.reportStateSvc.save(this.reportSlug, this.moduleName, {
        filterRules: rules,
        advancedFilterOpen: false
      });
    }
    this.filterRulesChange.emit(rules);
    this.filterPanelOpen = false;
    this.cdr.markForCheck();
  }

  onFilterPanelClosed(): void {
    this.filterPanelOpen = false;
    if (this.moduleName && this.reportSlug) {
      this.reportStateSvc.save(this.reportSlug, this.moduleName, { advancedFilterOpen: false });
    }
  }

  // â”€â”€ Refresh & clear â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  onRefreshClick(): void { this.refresh.emit(); }

  onClearFiltersClick(): void {
    this.dateFilterRef?.reset(this.defaultDateMode);
    this.lastAppliedRules = [];
    this.activeRuleCount = 0;
    if (this.moduleName && this.reportSlug) {
      this.reportStateSvc.clear(this.reportSlug, this.moduleName);
    }
    this.clearFilters.emit();
    this.cdr.markForCheck();
  }

  // â”€â”€ Favourite â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  onFavouriteClick(): void {
    if (!this.moduleName || !this.reportSlug) return;
    if (this.isFavourite) {
      // Simple remove â€” remove the entire favourite entry.
      const entry = this._buildFavouriteEntry([]);
      this.favSvc.toggle(entry);
      this.snackBar.open('Removed from favourites', 'OK', { duration: 3000 });
      this.showFavNamePrompt = false;
    } else {
      // Show the inline "name your config" prompt.
      this.favConfigName = this.reportName || this.title || '';
      this.showFavNamePrompt = true;
    }
    this.cdr.markForCheck();
  }

  saveAsFavourite(): void {
    if (!this.moduleName || !this.reportSlug) return;
    const state = this._buildCurrentState();
    const config: FavouriteConfig = {
      id: ReportStateService.generateId(),
      name: (this.favConfigName || this.reportName || this.title).trim(),
      createdAt: Date.now(),
      updatedAt: Date.now(),
      state
    };
    const entry = this._buildFavouriteEntry([config]);
    const action = this.favSvc.toggle(entry); // adds it (was not a favourite)
    if (action === 'removed') {
      // Edge case: was toggled to removed â€” re-add with the config.
      this.favSvc.toggle(entry);
    }
    this.showFavNamePrompt = false;
    this.favConfigName = '';
    this.snackBar.open('Added to favourites', 'OK', { duration: 3000 });
    this.cdr.markForCheck();
  }

  saveAnotherFavouriteConfig(): void {
    if (!this.moduleName || !this.reportSlug || !this.isFavourite) return;
    const state = this._buildCurrentState();
    const config: FavouriteConfig = {
      id: ReportStateService.generateId(),
      name: (this.favConfigName || this.reportName || this.title).trim(),
      createdAt: Date.now(),
      updatedAt: Date.now(),
      state
    };
    this.favSvc.addConfig(this.reportSlug, this.moduleName, config);
    this.showFavNamePrompt = false;
    this.favConfigName = '';
    this.snackBar.open('Saved as a new favourite configuration', 'OK', { duration: 3000 });
    this.cdr.markForCheck();
  }

  cancelFavPrompt(): void {
    this.showFavNamePrompt = false;
    this.favConfigName = '';
    this.cdr.markForCheck();
  }

  get favConfigs(): FavouriteConfig[] {
    if (!this.moduleName || !this.reportSlug) return [];
    return this.favSvc.getConfigs(this.reportSlug, this.moduleName);
  }

  // â”€â”€ Export â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  exportXlsx(): void {
    const cols = this.buildExportColumns();
    if (!cols.length) return;
    this.exportSvc.exportXlsx(cols, this.rows ?? [], this.buildContext());
  }

  exportCsv(): void {
    const cols = this.buildExportColumns();
    if (!cols.length) return;
    this.exportSvc.exportCsv(cols, this.rows ?? [], this.buildContext());
  }

  async openEmailDialog(): Promise<void> {
    const data: EmailReportDialogData = {
      reportSlug: this.reportSlug,
      reportName: this.reportName || this.title,
      defaultSubject: `${this.reportName || this.title} â€” ${this.academicYearLabel || ''}`.trim(),
      defaultBody: `Please find attached the ${this.reportName || this.title} report.`,
      defaultFormat: 'xlsx'
    };

    const ref = this.dialog.open<EmailReportDialogComponent, EmailReportDialogData, EmailReportDialogResult>(
      EmailReportDialogComponent,
      { data, autoFocus: false, panelClass: ['sms-dialog-pane', 'sms-dialog-md'] }
    );

    const result = await firstValueFrom(ref.afterClosed());
    if (!result) return;

    this.emailing = true;
    this.cdr.markForCheck();

    this.emailSvc.send({
      schoolId: this.schoolId,
      academicYearId: this.academicYearId,
      reportSlug: this.reportSlug,
      reportName: this.reportName || this.title,
      to: result.to,
      cc: result.cc,
      subject: result.subject,
      body: result.body,
      format: result.format,
      filters: this.currentFilters,
      columns: this.buildExportColumns().map(c => c.key)
    }).subscribe({
      next: () => {
        this.emailing = false;
        this.snackBar.open('Report emailed successfully.', 'OK', { duration: 4000 });
        this.cdr.markForCheck();
      },
      error: () => {
        this.emailing = false;
        this.snackBar.open('Failed to send email. Please try again.', 'Dismiss', { duration: 5000 });
        this.cdr.markForCheck();
      }
    });
  }

  // â”€â”€ Private helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  private loadAcademicYears(): void {
    this.academic.getAcademicYearList().subscribe({
      next: (res: any) => {
        const list = res?.data ?? res ?? [];
        this.academicYearList = Array.isArray(list) ? list : [];
        this.cdr.markForCheck();
      },
      error: () => { this.academicYearList = []; this.cdr.markForCheck(); }
    });
  }

  private syncOverlayState(): void {
    if (!this.api) return;
    if (this.loading) { this.api.showLoadingOverlay(); return; }
    if (!this.rows || this.rows.length === 0) { this.api.showNoRowsOverlay(); return; }
    this.api.hideOverlay();
  }

  private applyVisibility(): void {
    if (!this.api || this.visibleKeys.size === 0) return;
    // Recurses into column groups so banded reports can toggle their child columns too.
    const walk = (defs: (ColDef | ColGroupDef)[]): void => {
      for (const c of defs) {
        const children = (c as ColGroupDef).children;
        if (Array.isArray(children)) { walk(children); continue; }
        const f = ((c as ColDef).field ?? '').toString();
        if (!f) continue;
        this.api!.setColumnsVisible([f], this.visibleKeys.has(f));
      }
    };
    walk(this.colDefs);
  }

  private _loadSavedState(): ReportStateV1 | null {
    if (!this.moduleName || !this.reportSlug) return null;
    return this.reportStateSvc.load(this.reportSlug, this.moduleName);
  }

  private _buildCurrentState(): ReportStateV1 {
    return {
      _v: 1,
      _ts: Date.now(),
      visibleColumns: Array.from(this.visibleKeys),
      dateFilterMode: this._lastDateRange?.mode,
      dateFromDate: this._lastDateRange?.fromDate || undefined,
      dateToDate: this._lastDateRange?.toDate || undefined,
      academicYearId: this.academicYearId || undefined,
      pageSize: this.api?.paginationGetPageSize() || undefined,
      advancedFilterOpen: this.filterPanelOpen,
      filterRules: this.lastAppliedRules.length ? this.lastAppliedRules : undefined
    };
  }

  private _buildFavouriteEntry(configs: FavouriteConfig[]): FavouriteReportEntry {
    return {
      slug: this.reportSlug,
      module: this.moduleName,
      moduleLabel: this.moduleLabel || this.moduleName,
      name: this.reportName || this.title,
      routePath: window.location.pathname,
      description: this.reportDescription,
      configs
    };
  }

  private _refreshFavouriteStatus(): void {
    this.isFavourite = this.favSvc.isFavourite(this.reportSlug, this.moduleName);
  }

  private buildExportColumns(): ExportColumn[] {
    const visibleSet = this.visibleKeys.size > 0
      ? this.visibleKeys
      : new Set(this.columns.filter(c => c.defaultVisible !== false || c.pinned).map(c => c.key));
    if (this.exportColumns) return this.exportColumns.filter(c => visibleSet.has(c.key));
    return this.columns.filter(c => visibleSet.has(c.key)).map(c => ({ key: c.key, label: c.label }));
  }

  private buildContext(): ExportContext {
    return {
      reportSlug: this.reportSlug,
      reportName: this.reportName || this.title,
      schoolCode: this.schoolCode,
      academicYear: this.academicYearLabel,
      filterSummary: this.filterSummary
    };
  }
}
